import { useState, useEffect, useCallback } from 'react';
import Kedo from '@/Kedo';
import { Block, Pager } from '../types';
import { getBlockLimit, getSortParams } from '../utils/helpers';

interface FetchParams {
    defDossier?: number | null;
    environment?: number;
    page?: number;
    limit?: number;
    view?: string;
    sort?: number[];
    sortOrder?: string[];
    linkId?: number;
    embedded?: number;
    dossierQuery?: number | null;
}

interface UseBlockDataOptions {
    kedo: Kedo;
    block: Block;
    link?: number;
    dossier?: { id: number; def_dossier_id?: number };
    query?: number | null;
    enabled?: boolean;
}

interface UseBlockDataResult<T> {
    data: T | null;
    loading: boolean;
    error: Error | null;
    pager: Pager | null;
    page: number;
    setPage: (page: number) => void;
    refetch: () => Promise<void>;
}

export const useBlockData = <T>({
    kedo,
    block,
    link,
    dossier,
    query,
    enabled = true,
}: UseBlockDataOptions): UseBlockDataResult<T> => {
    const [data, setData] = useState<T | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [pager, setPager] = useState<Pager | null>(null);
    const [page, setPage] = useState(0);

    const fetchData = useCallback(async () => {
        const environment = kedo.env().getEnvironment();
        if (!environment || !enabled) return;

        const params: FetchParams = {
            defDossier: block.def_dossier_id,
            environment: environment.id,
            page,
            limit: getBlockLimit(block),
        };

        if (block.settings?.view_name) {
            params.view = block.settings.view_name;
        }

        const sortParams = getSortParams(block.settings?.sort);
        if (sortParams) {
            params.sort = sortParams.sort;
            params.sortOrder = sortParams.sortOrder;
        }

        if (link) {
            params.linkId = link;
            params.embedded = dossier?.def_dossier_id ? dossier.id : dossier?.id;
        }

        if (query) {
            params.dossierQuery = query;
        }

        if (block.dossier_query_id) {
            params.dossierQuery = block.dossier_query_id;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await kedo.api().get(kedo.api().getContentDossierEndpoint(), { params });
            setData(response.data.results);
            setPager(response.data.pager);
        } catch (err) {
            setData(null);
            setPager(null);
            setError(err as Error);
        } finally {
            setLoading(false);
        }
    }, [kedo, block, link, dossier, query, page, enabled]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return {
        data,
        loading,
        error,
        pager,
        page,
        setPage,
        refetch: fetchData,
    };
};
